import { useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Formik, Form, Field } from "formik";
import {
  Col,
  Container,
  Row,
  Button,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  FormFeedback,
} from "reactstrap";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import * as Yup from "yup";
import moment from "moment";

import FormHelpers from "../../helpers/FormHelpers";
import InfoTooltip from "../UI/Progress/InfoTooltip";
import ScrollToFieldError from "./Validation/ScrollToFieldError";
import CardPreview from "../Cards/CardPreview";

import { configuratorActions } from "../../store/configurator-slice";
import { cardPreviewActions } from "../../store/card-preview-slice";

function PersonalizeForm() {
  const dispatch = useDispatch();

  // get current preview state
  const cardSignaturePreview = useSelector(
    (state) => state.cardPreview.CardSignature
  );
  const showCardGallery = useSelector(
    (state) => state.cardPreview.ShowCardGallery
  );
  const showVerseGallery = useSelector(
    (state) => state.cardPreview.ShowVerseGallery
  );

  // get current request state
  const currentRequest = useSelector(
    (state) => state.configurator.ConfiguratorRequest
  );
  const requestDetails = currentRequest.DonorRequest;

  const isGregorian = requestDetails.MassTypeValue == "gregorian";

  // prepare date features, closed, and restricted dates
  const minDateSelect = moment(currentRequest.ServerDateTime).add(1, "days");
  const maxDateSelect = moment(currentRequest.ServerDateTime).add(1, "year");

  const restrictedDates = currentRequest.Lists.HolidayDates;
  const restrictedDatesArray = restrictedDates.map((item) => {
    return moment(item.HolidayDate).toDate();
  });

  // init state for form fields
  const showCardSignature = !requestDetails.DoNotSignCard;
  const [showMailDate, setShowMailDate] = useState(false);

  // init form validation schema
  const validationRules = {
    cardSignature: Yup.string().when("doNotSignCard", (doNotSignCard) => {
      if (!doNotSignCard) return Yup.string().required("Required");
    })
  };

  if(isGregorian && requestDetails.CardTypeValue != 'sendtome') {
    validationRules.recipientRelationDeceased = Yup.string().required("Required");
  }

  const formSchema = Yup.object().shape(validationRules);

  const formRef = useRef();
  const toggleShowCardSignatureHandler = (e) => {
    dispatch(configuratorActions.setShowCardSignature(e.target.checked));
    console.log(e.target.checked);  
    if(e.target.checked) {
      formRef.current.values.cardSignature = '';
      dispatch(
        cardPreviewActions.setFieldValue({
          key: "CardSignature",
          value: '',
        })
      );
      formRef.current.values.doNotSignCard = true;
      dispatch(configuratorActions.setShowCardSignature(true));
    } else {
      formRef.current.values.doNotSignCard = false;
      dispatch(configuratorActions.setShowCardSignature(false));
    }
  };

  const toggleShowMailDateHandler = () => {
    setShowMailDate(true);
  };

  const formSubmitHandler = (values) => {
    dispatch(configuratorActions.setPersonalize(values));
    FormHelpers.scrollToTop();
  };

  const previousClickHandler = (values) => {

    if(requestDetails.CardSignature != cardSignaturePreview) {
      dispatch(
        cardPreviewActions.setFieldValue({
          key: "CardSignature",
          value: '',
        })
      );
    }

    dispatch(configuratorActions.setFormStep("Request"));
    FormHelpers.scrollToTop();
  };

  const inputFocusHandler = (e) => {
    dispatch(cardPreviewActions.setFieldFocus(e.target.name));
  };

  const inputBlurHandler = (e) => {
    dispatch(cardPreviewActions.setFieldFocus(""));
  };

  // toggle more cards modal
  const toggleCardsModal = (e) => {
    dispatch(cardPreviewActions.setShowCardGallery(!showCardGallery));
  };

  // toggler more verses modal
  const toggleVersesModal = (e) => {
    dispatch(cardPreviewActions.setShowVerseGallery(!showVerseGallery));
  };

  const isWeekday = (date) => {
    if(requestDetails.CardTypeValue == 'ecard')
      return true;

    const day = date.getDay();
    return !(day === 0 || day === 6); // 0 represents Sunday and 6 represents Saturday
  };

  let mailDate = "";
  if (requestDetails.MailDate) {
    mailDate = moment(requestDetails.MailDate).valueOf();
  }

  const mailDateRef = useRef(null);
  const openMailDatePicker = () => mailDateRef.current.setOpen(true);

  return (
    <div className="cfg-input-form cfg-personalize" data-step="personalize">
      <Formik
        initialValues={{
          cardSignature: requestDetails.CardSignature,
          mailDate: mailDate,
          doNotSignCard: requestDetails.DoNotSignCard,
          recipientRelationDeceased: requestDetails.RecipientRelationDeceased
        }}
        innerRef={formRef}
        validationSchema={formSchema}
        onSubmit={formSubmitHandler}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
        }) => (
          <Form className="text-start">
            <ScrollToFieldError />
            <Container className="cfg-form-section">
              <Row className="mobile-only">
                <Col xs="12">
                  <CardPreview
                    zoom={0.29}
                    showCard={true}
                    showEnvelope={false}
                    preview={true}
                  />
                </Col>
              </Row>

              <Row className="mb-2 desktop-only">
                <Col xs="12">
                  
                  {!isGregorian && (<h4>
                    You can change the{" "}
                    <a href="#" onClick={toggleCardsModal}>
                      card
                    </a>
                    ,{" "}
                    <a href="#" onClick={toggleVersesModal}>
                      verse
                    </a>.
                  </h4>)}
                </Col>
              </Row>

              <Row className="mobile-only personalize-mobile-buttons mb-4">
                <Col xs="12" style={{ overflow: 'hidden'}}>
                  <button
                    type="button"
                    onClick={toggleCardsModal}
                    className="btn btn-primary btn-sm cfg-btn-more-cards float-start"
                  >
                    More Cards
                  </button>
                  {" "}
                  {!isGregorian && (<button
                    type="button"
                    onClick={toggleVersesModal}
                    className="btn btn-primary btn-sm cfg-btn-more-verses float-end"
                  >
                    More Verses
                  </button>)}
                </Col>
              </Row>

              {!values.doNotSignCard && (
                <Row className="mb-2">
                  <Col xs="12">
                  <InfoTooltip
                    id="personalizeHelp"
                    title="Help"
                    text="Express your feelings to the recipient by typing up to three lines of thirty-two characters. People often precede their name with thoughts such as “Our deepest sympathy”, “Get well soon”, “Thoughts and prayers” etc."
                  />
                    <FormGroup>
                      <h4 className="required">
                        Type your sentiment and sign your name (e.g., "With
                        Love, Bob and Mary"):
                      </h4>

                      <Input
                        name="cardSignature"
                        type="textarea"
                        id="cardSignature"
                        datamax="96"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        value={values.cardSignature}
                        rows="3"
                        cols="32"
                        className="cfg-card-signature-input"
                        placeholder="3 lines of 32 characters each"
                        onKeyUp={FormHelpers.handleMultilineKeyUp}
                        onKeyDown={FormHelpers.handleMultilineKeyDown}
                        onPaste={FormHelpers.handleMultilineUpdate}
                        onChange={(e) => {
                          FormHelpers.handleMultilineUpdate(e);
                          handleChange(e);
                          dispatch(
                            cardPreviewActions.setFieldValue({
                              key: "CardSignature",
                              value: e.target.value,
                            })
                          );
                        }}
                        onFocus={inputFocusHandler}
                        onBlur={(e) => {
                          FormHelpers.handleMultilineUpdate(e);
                          handleBlur(e);
                          inputBlurHandler();
                        }}
                        invalid={Boolean(
                          touched["cardSignature"] && errors["cardSignature"]
                        )}
                      />

                      {touched["cardSignature"] && errors["cardSignature"] ? (
                        <FormFeedback>{errors["cardSignature"]}</FormFeedback>
                      ) : (
                        ""
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              )}
              {requestDetails.CardTypeValue === "sendtome" && (
                <Row className="mb-2">
                  <Col xs="12">
                    <FormGroup>
                      <label>
                        <Field
                          type="checkbox"
                          name="doNotSignCard"
                          onClick={toggleShowCardSignatureHandler}
                        />{" "}
                        I will sign the card myself.
                      </label>
                    </FormGroup>
                  </Col>
                </Row>
              )}


              {isGregorian && requestDetails.CardTypeValue != 'sendtome' && (<Row className="mb-2">
                  <Col xs="12">
                  <InfoTooltip
                      id="recipientRelationDeceasedHelp"
                      title="Help"
                      text="Please select the card recipient's relationship to the deceased."
                    />
                    <FormGroup>
                      <label className="required">Recipient's Relationship to the Deceased:</label>
                      <select
                        name="recipientRelationDeceased"
                        value={values.recipientRelationDeceased}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-control ${
                          errors.recipientRelationDeceased && touched.recipientRelationDeceased
                            ? "is-invalid"
                            : ""
                        }`}
                      >
                        <option value="">Please select...</option>
                        <option>Brother/Sister</option>
                        <option>Grandparent</option>
                        <option>Grandson/Granddaughter</option>
                        <option>Nephew/Niece</option>
                        <option>Parent</option>
                        <option>Son/Daughter</option>
                        <option>Spouse</option>
                        <option>Uncle/Aunt</option>
                        <option>Other</option>
                      </select>
                      <div
                        className={`${
                          errors.recipientRelationDeceased && touched.recipientRelationDeceased
                            ? "is-invalid"
                            : ""
                        }`}
                      ></div>
                      {errors.recipientRelationDeceased && touched.recipientRelationDeceased && (
                        <div className="invalid-feedback">
                          {errors.recipientRelationDeceased}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>)}


              <Row className="mb-2">
                <Col xs="12">
                  <InfoTooltip
                    id="mailDateHelp"
                    title="Help"
                    text="You may select any business day within the next year."
                  />
                  <h4>Mail Date:</h4>
                  <small>
                    <span>
                      We will send your card after you checkout.
                    </span>
                    {!showMailDate && !values.mailDate && (
                      <span>
                        To request a specific date{" "}
                        <a href="#" onClick={toggleShowMailDateHandler}>
                          click here
                        </a>
                        .
                      </span>
                    )}
                  </small>
                  {(showMailDate || values.mailDate) && (
                    <FormGroup>
                      <InputGroup>
                        <InputGroupText>
                          <i className="fas fa-calendar-alt" onClick={openMailDatePicker}></i>
                        </InputGroupText>
                        <DatePicker
                          id="mailDate"
                          name="mailDate"
                          ref={mailDateRef}
                          className={`form-control ${
                            errors.mailDate && touched.mailDate
                              ? "is-invalid"
                              : ""
                          }`}
                          onKeyDown={e => e.preventDefault()}
                          dateFormat="MM/dd/yyyy"
                          value={values.mailDate}
                          selected={values.mailDate}
                          onChange={(date, e) => {
                            handleChange(e);
                            if (date !== null) {
                              setFieldValue("mailDate", date);
                            }
                          }}
                          onChangeRaw={(e) => {
                            setFieldTouched("mailDate", true, true);
                          }}
                          onBlur={handleBlur}
                          shouldCloseOnSelect={true}
                          minDate={minDateSelect.toDate()}
                          maxDate={maxDateSelect.toDate()}
                          excludeDates={restrictedDatesArray}
                          filterDate={isWeekday}
                          peekNextMonth
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          autoComplete="off"
                        />
                        <button
                          onClick={(e) => {
                            setFieldValue("mailDate", "");
        
                          }}
                          type="button"
                          className="react-datepicker__close-icon"
                          aria-label="Close"
                          tabIndex="-1"
                        ></button>
                      </InputGroup>
                      <div
                        className={`${
                          errors.mailDate && touched.mailDate
                            ? "is-invalid"
                            : ""
                        }`}
                      ></div>
                      {errors.mailDate && touched.mailDate && (
                        <div className="invalid-feedback">
                          {errors.mailDate}
                        </div>
                      )}
                    </FormGroup>
                  )}
                </Col>
              </Row>
              {/* <DebugOutput
                    errors={errors}
                    values={FormHelpers.getValues(values)}
                  /> */}
            </Container>
            <Button
              type="button"
              size="sm"
              color="primary"
              onClick={previousClickHandler}
              className="btn-secondary"
            >
              Previous
            </Button>{" "}
            <Button type="submit" size="sm" color="primary" className="float-end">
              Next
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default PersonalizeForm;
